
import { Eye, Trash2 } from 'lucide-react';
import { Member } from '../App';
// If you haven't installed xlsx, run: npm install xlsx
import * as XLSX from 'xlsx';
import { useState } from 'react';

type MembersTableProps = {
  members: Member[];
  onView: (member: Member) => void;
  onDelete: (id: number) => void;
};



const PAGE_SIZE = 10;

const MembersTable: React.FC<MembersTableProps> = ({ members, onView, onDelete }) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(members.length / PAGE_SIZE);
  const paginatedMembers = members.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExportExcel = () => {
    // Prepare data for export (remove photoUrl for Excel, flatten fields)
    const exportData = members.map(({ photoUrl, ...rest }: Member) => rest);
    if (typeof XLSX === 'undefined' || !XLSX.utils) {
      alert('xlsx package not found. Please run: npm install xlsx');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members');
    XLSX.writeFile(wb, 'gym_members.xlsx');
  };

  return (
    <div className="overflow-x-auto">
      <div className="flex justify-between items-center mb-2">
        <div></div>
        <button
          onClick={handleExportExcel}
          className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors duration-200 shadow-sm"
        >
          Export to Excel
        </button>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Name</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Contact</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Gender</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Membership</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Plan</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Join Date</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Payment</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {paginatedMembers.map((member: Member) => (
            <tr key={member.id} className="hover:bg-gray-50 transition-colors duration-200">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  {member.photoUrl ? (
                    <img src={member.photoUrl} alt="Profile" className="h-10 w-10 rounded-full object-cover mr-3" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold text-lg shadow-sm mr-3">
                      {member.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="text-base font-bold text-gray-900 tracking-tight">{member.name}</div>
                    <div className="text-gray-500 text-xs font-medium">ID: #{member.id}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-gray-900 font-semibold">{member.phone}</div>
                <div className="text-gray-500 font-medium text-xs">{member.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">{member.gender}</td>
              <td className="px-6 py-4 whitespace-nowrap">{member.membershipType}</td>
              <td className="px-6 py-4 whitespace-nowrap">{member.plan}</td>
              <td className="px-6 py-4 whitespace-nowrap">{new Date(member.joinDate).toLocaleDateString()}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${member.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                  {member.paymentStatus}
                </span>
                <div className="text-gray-500 text-xs mt-1">{member.paymentMethod}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onView(member)}
                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(member.id)}
                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200"
                    title="Delete Member"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Pagination Controls */}
      <div className="flex justify-end items-center gap-2 mt-4">
        <button
          onClick={() => setPage((p:any) => Math.max(1, p - 1))}
          disabled={page === 1}
          className={`px-3 py-1 rounded-lg font-semibold ${page === 1 ? 'bg-gray-200 text-gray-400' : 'bg-gray-900 text-white hover:bg-gray-800'} transition-colors duration-200`}
        >
          Prev
        </button>
        <span className="text-sm font-medium text-gray-700">Page {page} of {totalPages || 1}</span>
        <button
          onClick={() => setPage((p:any) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages || totalPages === 0}
          className={`px-3 py-1 rounded-lg font-semibold ${page === totalPages || totalPages === 0 ? 'bg-gray-200 text-gray-400' : 'bg-gray-900 text-white hover:bg-gray-800'} transition-colors duration-200`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default MembersTable;
